import React, { PureComponent, Fragment } from 'react';
import { Map } from 'immutable';
import PropTypes from 'prop-types';
import memoizeOne from 'memoize-one';
import Translator from 'translator';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import { FHETable } from 'data/components';
import { Toastr } from 'data/common';
import { withStyles, Menu } from '@material-ui/core';
import { Build as ActionIcon, Refresh as RefreshIcon, Add as AddIcon, RemoveRedEye as ViewIcon } from '@material-ui/icons';
import tools from 'data/common/tools';
import { getHomes } from '../../data/Homes/actions';
import ActionsMenu from './components/ActionsMenu';
import selectors from './selectors';

const logoAplus = require('images/icons/logo_aplus.png');
const logoEnerdom = require('images/icons/logo_enerdom.png');
const logoFhe = require('images/icons/logo_fhe.png');
const logoSoleco = require('images/icons/logo_soleco.png');
const logoViebrock = require('images/icons/logo_viebrock.png');

class Home extends PureComponent {
    static propTypes = {
        getHomes: PropTypes.func.isRequired,
        Homes: PropTypes.object.isRequired,
        history: PropTypes.object.isRequired,
        profile: PropTypes.object.isRequired,
        initialized: PropTypes.bool.isRequired,
        classes: PropTypes.object.isRequired
    };

    constructor(props) {
        super(props);
        const { initialized } = props;
        this.state = {
            callbackRowActionLoading: null,
            loading: !initialized,
            openedMenu: false,
            homeBox: new Map(),
            target: null
        };
    }

    componentDidMount() {
        const { initialized } = this.props;
        if (!initialized) this.refreshHomes();
    }

    refreshHomes = () => {
        const { getHomes } = this.props;
        this.setState({ loading: true });
        return getHomes()
            .catch(err => {
                const message = Translator.translateWithFallback(`api.message.${err.message ? err.message : err}`, 'api.message.default');
                Toastr.error(message);
            })
            .finally(() => {
                this.setState({ loading: false });
            });
    };

    navigateToAddHome = () => {
        const { history } = this.props;
        return history.push({ pathname: 'home/add' });
    };
    navigateToHomeDetail = rowData => {
        const { history } = this.props;
        return history.push({ pathname: `home/detail/${rowData.homeId}` });
    };

    hasRightToAction = rowData => {
        const { profile, history } = this.props;
        const userRights = profile.getIn(['user_rights', history.env], []);
        const rightsAction = ['updateBoxFHE', 'refreshBoxFHE', 'restartBoxFHE', 'startVpn', 'stopVpn'];
        return rowData.macAddress !== '' && (profile.get('admin') || userRights.some(v => rightsAction.includes(v)));
    };

    closeMenuState = () => {
        this.setState({ openedMenu: false });
    };

    renderActions = memoizeOne((profile, Homes) => {
        const { history } = this.props;
        let actions = {
            row: [
                {
                    icon: ActionIcon,
                    tooltip: Translator.translate(`generic.home.boxActions`, Translator.translate(`${history.environment}.centerName`)),
                    hasRight: this.hasRightToAction,
                    onClick: (rowData, callbackRowActionLoading, event) => {
                        this.setState({
                            target: event.currentTarget,
                            callbackRowActionLoading,
                            openedMenu: true,
                            homeBox: Homes.getIn([rowData.homeId.toString()], new Map())
                        });
                    }
                }
            ],
            toolbar: [
                {
                    icon: RefreshIcon,
                    tooltip: Translator.translate('generic.home.table.globalAction.refresh.tooltip'),
                    onClick: this.refreshHomes
                }
            ]
        };
        if (profile.get('admin') || profile.getIn(['user_rights', 'fhe'], []).includes('homeAdd')) {
            actions.toolbar.push({
                icon: AddIcon,
                tooltip: Translator.translate('generic.home.table.globalAction.add.tooltip'),
                onClick: this.navigateToAddHome
            });
        }
        if (profile.get('admin') || profile.getIn(['user_rights', 'fhe'], []).includes('homeDetails')) {
            actions.row.push({
                icon: ViewIcon,
                onClick: this.navigateToHomeDetail,
                tooltip: Translator.translate('general.showHome')
            });
        }
        return actions;
    });

    renderData = memoizeOne(Homes => {
        let result = [];
        Homes.map((Home, HomeId) => {
            result.push({
                homeId: parseInt(HomeId),
                boxId: Home.get('boxId') ? parseInt(Home.get('boxId')) : '',
                label: Home.getIn(['label'], ''),
                brand: Home.getIn(['brand'], ''),
                macAddress: Home.get('macAddress')
                    ? Home.get('macAddress')
                          .match(/.{1,2}/g)
                          .join(':')
                    : '',
                firmwareVersion: Home.getIn(['firmwareVersion'], ''),
                boxIsConnected: Home.getIn(['boxIsConnected'], ''),
                state:
                    Home.getIn(['boxIsConnected'], '') === ''
                        ? ''
                        : Home.get('boxIsConnected')
                        ? Translator.translate('general.connected')
                        : Translator.translate('general.disconnected'),
                freeSpace: Home.get('freeSpace') ? parseInt(Home.get('freeSpace')) : '',
                postalCode: Home.getIn(['postalCode'], ''),
                timeLag: Home.getIn(['timeDiff'], ''),
                lastConnection: Home.getIn(['lastConnection'], ''),
                upgrade: Home.get('upgrade')
            });
        });
        return result;
    });

    renderColumns = memoizeOne(profile => {
        const { classes } = this.props;

        return [
            {
                title: Translator.translate(`general.table.column.boxId`, Translator.translate(`fhe.centerName`)),
                name: 'boxId',
                width: 90,
                fixedLeft: true
            },
            {
                title: Translator.translate('general.table.column.homeId'),
                name: 'homeId',
                width: 90,
                defaultSort: 'desc',
                fixedLeft: true
            },
            {
                title: Translator.translate('general.table.column.label'),
                name: 'label',
                action: (profile.get('admin') || profile.getIn(['user_rights', 'fhe'], []).includes('homeDetails')) && {
                    onClick: this.navigateToHomeDetail,
                    tooltip: Translator.translate('general.showHome')
                }
            },
            {
                title: Translator.translate('general.table.column.brand'),
                name: 'brand',
                render: (row, data, value) => {
                    let res = null;
                    switch (value) {
                        case 'aplusbox':
                            res = <img src={logoAplus} alt="Aplusbox" className={classes.brandIcon} />;
                            break;
                        case 'enerdom':
                            res = <img src={logoEnerdom} alt="Enerdom" className={classes.brandIcon} />;
                            break;
                        case 'fullhomeenergy':
                            res = <img src={logoFhe} alt="Fullhomeenergy" className={classes.brandIcon} />;
                            break;
                        case 'solecofrance':
                            res = <img src={logoSoleco} alt="Solecofrance" className={classes.brandIcon} />;
                            break;
                        case 'viebrockhaus':
                            res = <img src={logoViebrock} alt="Viebrockhaus" className={classes.brandIcon} />;
                            break;
                    }
                    return res;
                }
            },
            {
                title: Translator.translate('general.table.column.macAddress'),
                name: 'macAddress',
                width: 150,
                customFilter: tools.filterMacAddress
            },
            {
                title: Translator.translate('general.table.column.postalCode'),
                name: 'postalCode'
            },
            {
                title: Translator.translate('general.table.column.firmwareVersion'),
                name: 'firmwareVersion',
                customSort: tools.sortVersion
            },
            {
                title: Translator.translate('general.table.column.freeSpace'),
                name: 'freeSpace',
                render: (row, data, value) => {
                    if (!value) return null;
                    return <span className={value > 75 ? classes.lowDiskSpace : undefined}>{value}%</span>;
                }
            },
            {
                title: Translator.translate('general.table.column.timeLag'),
                name: 'timeLag'
            },
            {
                title: Translator.translate('general.table.column.lastConnection'),
                name: 'lastConnection',
                customSort: tools.sortDate
            },
            {
                title: Translator.translate('general.table.column.state'),
                name: 'state',
                render: (row, data, value) => {
                    if (!value) return null;
                    return (
                        <span className={value === Translator.translate('general.connected') ? classes.connected : classes.disconnected}>
                            {value}
                        </span>
                    );
                }
            },
            {
                title: Translator.translate('general.table.column.upgrade'),
                name: 'upgrade',
                render: (row, data, value) => {
                    if (!Number.isInteger(value)) return null;
                    return (
                        <span className={value === 0 ? classes.disconnected : undefined}>{Translator.translate(`general.data.mode.${value}`)}</span>
                    );
                }
            }
        ];
    });

    render() {
        const { loading, target, openedMenu, homeBox, callbackRowActionLoading } = this.state;
        const { Homes, profile } = this.props;
        return (
            <Fragment>
                {openedMenu && target !== null && homeBox.get('macAddress') !== undefined && (
                    <Menu anchorEl={target} keepMounted open={openedMenu} onClose={this.closeMenuState} onClick={this.closeMenuState}>
                        <div>
                            <ActionsMenu homeBox={homeBox} handleLoading={callbackRowActionLoading} />
                        </div>
                    </Menu>
                )}
                <FHETable
                    idTable="homeList"
                    columns={this.renderColumns(profile)}
                    data={this.renderData(Homes)}
                    actions={this.renderActions(profile, Homes)}
                    title={Translator.translate('generic.home.table.title')}
                    emptyDataSourceMessage={Translator.translate('generic.home.table.body.emptyDataSourceMessage')}
                    isLoading={loading}
                />
            </Fragment>
        );
    }
}

function mapStateToProps(state) {
    return {
        profile: selectors.getProfile(state),
        Homes: selectors.getHomesWithBoxes(state),
        initialized: selectors.getHomesInitialized(state)
    };
}

const styles = theme => ({
    lowDiskSpace: {
        color: theme.palette.error.main
    },
    checkIcon: {
        color: theme.palette.success.main
    },
    connected: {
        color: theme.palette.success.main
    },
    disconnected: {
        color: theme.palette.error.main
    },
    freeSpaceBarRoot: {
        height: 10
    },
    brandIcon: {
        width: 80
    }
});

export default withRouter(
    connect(
        mapStateToProps,
        dispatch => bindActionCreators({ getHomes }, dispatch)
    )(withStyles(styles, { withTheme: true })(Home))
);



// WEBPACK FOOTER //
// ./environments/fhe/scenes/Home/index.js